import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plug, 
  Key, 
  Globe, 
  CheckCircle, 
  AlertCircle, 
  Settings,
  ExternalLink,
  Database,
  Webhook
} from "lucide-react";
import { toast } from "sonner";

interface IntegrationConfig {
  name: string;
  enabled: boolean;
  baseUrl: string;
  apiKey: string;
  additionalSettings: Record<string, any>;
}

export default function APIIntegrations() {
  const [integrations, setIntegrations] = useState<Record<string, IntegrationConfig>>({
    canvas: {
      name: 'Canvas LMS',
      enabled: false,
      baseUrl: '',
      apiKey: '',
      additionalSettings: {
        accountId: '',
        syncInterval: '24',
        syncCourses: true,
        syncEnrollments: true,
        postCertificates: true
      }
    },
    moodle: {
      name: 'Moodle',
      enabled: false,
      baseUrl: '',
      apiKey: '',
      additionalSettings: {
        serviceName: 'moodle_mobile_app',
        syncInterval: '24',
        courseCategory: ''
      }
    },
    googleClassroom: {
      name: 'Google Classroom',
      enabled: false,
      baseUrl: 'https://classroom.googleapis.com',
      apiKey: '',
      additionalSettings: {
        clientId: '',
        clientSecret: '',
        scope: 'https://www.googleapis.com/auth/classroom.courses'
      }
    }
  });

  const [webhooks, setWebhooks] = useState([
    { id: '1', name: 'Course Completion', url: '', enabled: true, events: ['course.completed'] },
    { id: '2', name: 'Certificate Issued', url: '', enabled: false, events: ['certificate.issued'] },
    { id: '3', name: 'User Enrolled', url: '', enabled: false, events: ['user.enrolled'] }
  ]);

  const handleIntegrationUpdate = (key: string, updates: Partial<IntegrationConfig>) => {
    setIntegrations(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  };

  const testConnection = async (integrationKey: string) => {
    const integration = integrations[integrationKey];
    toast.info(`Testing connection to ${integration.name}...`);
    
    // Simulate API test
    setTimeout(() => {
      toast.success(`Successfully connected to ${integration.name}`);
    }, 2000);
  };

  const saveConfiguration = async () => {
    toast.success('API integration configuration saved successfully');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">API Integrations</h1>
        <p className="text-muted-foreground">Configure third-party system integrations and API connections</p>
      </div>

      <Tabs defaultValue="lms-integrations" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="lms-integrations">LMS Integrations</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="api-keys">API Management</TabsTrigger>
          <TabsTrigger value="logs">Integration Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="lms-integrations" className="space-y-6">
          <div className="grid gap-6">
            {Object.entries(integrations).map(([key, integration]) => (
              <Card key={key}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plug className="h-5 w-5" />
                      <CardTitle>{integration.name}</CardTitle>
                      <Badge variant={integration.enabled ? "secondary" : "outline"}>
                        {integration.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <Switch
                      checked={integration.enabled}
                      onCheckedChange={(enabled) => handleIntegrationUpdate(key, { enabled })}
                    />
                  </div>
                  <CardDescription>
                    Configure {integration.name} integration settings
                  </CardDescription>
                </CardHeader>
                {integration.enabled && (
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`${key}-url`}>Base URL</Label>
                        <Input
                          id={`${key}-url`}
                          value={integration.baseUrl}
                          onChange={(e) => handleIntegrationUpdate(key, { baseUrl: e.target.value })}
                          placeholder={`https://your-${key}-domain.com`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${key}-key`}>API Key</Label>
                        <Input
                          id={`${key}-key`}
                          type="password"
                          value={integration.apiKey}
                          onChange={(e) => handleIntegrationUpdate(key, { apiKey: e.target.value })}
                          placeholder="Enter API key"
                        />
                      </div>
                    </div>

                    {/* Canvas-specific settings */}
                    {key === 'canvas' && (
                      <>
                        <div>
                          <Label htmlFor="canvas-account">Account ID</Label>
                          <Input
                            id="canvas-account"
                            value={integration.additionalSettings.accountId}
                            onChange={(e) => handleIntegrationUpdate(key, {
                              additionalSettings: { ...integration.additionalSettings, accountId: e.target.value }
                            })}
                            placeholder="Canvas Account ID"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sync Options</Label>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={integration.additionalSettings.syncCourses}
                              onCheckedChange={(checked) => handleIntegrationUpdate(key, {
                                additionalSettings: { ...integration.additionalSettings, syncCourses: checked }
                              })}
                            />
                            <Label>Sync Courses</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={integration.additionalSettings.postCertificates}
                              onCheckedChange={(checked) => handleIntegrationUpdate(key, {
                                additionalSettings: { ...integration.additionalSettings, postCertificates: checked }
                              })}
                            />
                            <Label>Post Certificates to Canvas</Label>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex gap-2">
                      <Button onClick={() => testConnection(key)} variant="outline">
                        Test Connection
                      </Button>
                      <Button onClick={saveConfiguration}>
                        Save Configuration
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhook Configuration
              </CardTitle>
              <CardDescription>
                Configure webhooks for real-time data synchronization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {webhooks.map((webhook) => (
                <Card key={webhook.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-semibold">{webhook.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          Events: {webhook.events.join(', ')}
                        </p>
                      </div>
                      <Switch
                        checked={webhook.enabled}
                        onCheckedChange={(enabled) => {
                          setWebhooks(prev => prev.map(w => 
                            w.id === webhook.id ? { ...w, enabled } : w
                          ));
                        }}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`webhook-${webhook.id}`}>Webhook URL</Label>
                      <Input
                        id={`webhook-${webhook.id}`}
                        value={webhook.url}
                        onChange={(e) => {
                          setWebhooks(prev => prev.map(w => 
                            w.id === webhook.id ? { ...w, url: e.target.value } : w
                          ));
                        }}
                        placeholder="https://your-system.com/webhook/endpoint"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button className="w-full" variant="outline">
                Add New Webhook
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Key Management
              </CardTitle>
              <CardDescription>
                Manage API keys for external system access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  API keys provide programmatic access to the system. Keep them secure and rotate regularly.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Primary API Key</h4>
                        <p className="text-sm text-muted-foreground">Full system access</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          Regenerate
                        </Button>
                        <Button variant="outline" size="sm">
                          Copy
                        </Button>
                      </div>
                    </div>
                    <code className="block mt-2 p-2 bg-muted rounded text-sm font-mono">
                      pd_live_••••••••••••••••••••••••••••••••
                    </code>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">Read-Only API Key</h4>
                        <p className="text-sm text-muted-foreground">Read access only</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          Regenerate
                        </Button>
                        <Button variant="outline" size="sm">
                          Copy
                        </Button>
                      </div>
                    </div>
                    <code className="block mt-2 p-2 bg-muted rounded text-sm font-mono">
                      pd_readonly_••••••••••••••••••••••••••••
                    </code>
                  </CardContent>
                </Card>
              </div>

              <Button>
                Generate New API Key
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Integration Logs
              </CardTitle>
              <CardDescription>
                Monitor API integration activity and troubleshoot issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { time: '2:34 PM', event: 'Canvas sync completed', status: 'success', details: '156 users updated' },
                  { time: '1:45 PM', event: 'Certificate posted to Canvas', status: 'success', details: 'Course: Advanced Teaching Methods' },
                  { time: '12:30 PM', event: 'API rate limit reached', status: 'warning', details: 'Canvas API - retry in 60s' },
                  { time: '11:15 AM', event: 'Webhook delivery failed', status: 'error', details: 'Endpoint timeout: course.completed' },
                  { time: '10:22 AM', event: 'New API key generated', status: 'info', details: 'Read-only access key' }
                ].map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-3">
                      {log.status === 'success' && <CheckCircle className="h-4 w-4 text-success" />}
                      {log.status === 'warning' && <AlertCircle className="h-4 w-4 text-warning" />}
                      {log.status === 'error' && <AlertCircle className="h-4 w-4 text-destructive" />}
                      {log.status === 'info' && <Globe className="h-4 w-4 text-primary" />}
                      <div>
                        <p className="text-sm font-medium">{log.event}</p>
                        <p className="text-xs text-muted-foreground">{log.details}</p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{log.time}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <Button variant="outline" size="sm">
                  Export Logs
                </Button>
                <Button variant="outline" size="sm">
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}